import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2019 semantics GmbH
// Date        : $Date: 2026-05-25 21:10:12 +0200 (Mon, 25 May 2026) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 163262 $
// Purpose     : react file dropper based on
//               https://github.com/react-dropzone/react-dropzone
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Icon } from 'semantic-ui-react';
import { useDropzone } from 'react-dropzone';
import now from 'performance-now';
import clsx from 'clsx';
// ---Framework imports
import log from 'uix/logging';
import { get, size, mapItems, flatten, keys, values, filterItems } from 'ldash';
import { useState, useEffect, useCallback, useMemo, usePartContext, useTestableState } from 'uix/hooks';
import { Actions, Sel } from 'uix/registries';
import { VIcon } from 'uix/graphics';
import { prettyBytes } from 'uix/utils';
// ---Local Imports
import { XMenu as Menu, ProgressMenuItem } from './menu';
import styles from './dropper.css';
import { DebugFeature } from './debug';
// ---Globals
const MIME_IMAGE = [
    'image/tiff',
    'image/png',
    'image/jpeg',
    'image/jp2',
    'image/webp',
    'image/bpg',
    'image/jxr'
].join(',');
export const Dropper = ({ as = 'div', padded = false, moreMenu, progressMenu, onFilesChanged, singleFile = false, dialog = 'file', mimetypes = null, textInactive = null, textActive = 'Drop here ...', children, stopClickPropagation })=>{
    let { name: partName, type: partType } = usePartContext();
    let testPartId = partName || partType || 'global';
    let [showFiles, setShowFiles] = useState(false);
    let [dialogMode, setDialogMode] = useState(dialog) // or 'folder'
    ;
    let [openDeferred, setOpenDeferred] = useState(false);
    let [accept, setAccept] = useState(mimetypes);
    // uploaded => browser has received data from filesystem
    let [uploaded, setUploaded] = useTestableState({}, ()=>`dropper.${testPartId}.uploaded`);
    const onDrop = useCallback((acceptedFiles, fileRejections)=>{
        let local = {} // strange local scoping
        ;
        let _uploaded = uploaded;
        if (singleFile) {
            _uploaded = {}; // no additivity
            acceptedFiles = acceptedFiles.slice(-1); // only last
        }
        let update = (fpath, bytes)=>{
            // all paths are normalized with a leading slash
            if (!fpath.startsWith('/')) {
                fpath = `/${fpath}`;
            }
            local[fpath] = bytes;
            setUploaded({
                ..._uploaded,
                ...local,
                [fpath]: bytes
            });
        };
        acceptedFiles.forEach((file)=>{
            //~ log.debug('receive init: %o [%s]', file.path, prettyBytes(file.size))
            update(file.path, {
                state: 'init',
                size: file.size
            });
            const reader = new FileReader();
            reader.onabort = ()=>update(file.path, {
                    state: 'abort',
                    size: file.size
                });
            reader.onerror = ()=>update(file.path, {
                    state: 'error',
                    size: file.size
                });
            reader.onload = ()=>{
                // Do whatever you want with the file contents
                const binaryStr = reader.result;
                log.debug('receive done: %o', file.path);
                update(file.path, {
                    state: 'done',
                    bytes: binaryStr,
                    size: file.size
                });
            };
            reader.readAsArrayBuffer(file);
        });
        if (size(fileRejections) > 0) {
            log.warn('files rejected: %o', fileRejections.map((fr)=>fr.file));
        }
    }, [
        uploaded,
        singleFile
    ]);
    let doneFiles = filterItems(uploaded, (fpath, fdata)=>fdata.state == 'done');
    useEffect(()=>{
        if (onFilesChanged) {
            let files = doneFiles.map(([fpath, fdata])=>({
                    ...fdata,
                    path: fpath
                }));
            onFilesChanged(files);
        }
    }, [
        doneFiles.map((e)=>e[0]).join(',')
    ]);
    const { getRootProps, getInputProps, open: openDialog, isDragActive, isDragAccept, isDragReject } = useDropzone({
        onDrop,
        noDragEventsBubbling: true,
        accept,
        multiple: !singleFile
    });
    //~ let files = []
    let files = mapItems(uploaded, (fpath, { state, size, data })=>{
        return /*#__PURE__*/ _jsxs("div", {
            style: {
                paddingLeft: '10px',
                color: data ? 'black' : 'grey'
            },
            children: [
                fpath,
                ": ",
                state,
                " ",
                size && prettyBytes(size)
            ]
        }, fpath);
    });
    let openDialogWithMode = (mode)=>()=>{
            if (mode !== dialogMode) {
                setDialogMode(mode);
                // we need to wait for changed inputProps
                setOpenDeferred(true);
            } else {
                openDialog();
            }
        };
    useEffect(()=>{
        if (openDeferred) {
            openDialog();
            setOpenDeferred(false);
        }
    }, [
        openDeferred
    ]);
    let rootProps = getRootProps({
        className: clsx(styles.zone, as == 'div' && styles.flexdiv, isDragActive && styles.active, isDragAccept && styles.accept, isDragReject && styles.reject, padded && styles.padded)
    });
    let iprops = {
        accept
    };
    if (dialogMode == 'folder') {
        iprops = {
            ...iprops,
            webkitdirectory: 'true',
            mozdirectory: 'true',
            directory: 'true'
        };
    }
    let inputProps = getInputProps(iprops);
    let text = {
        active: textActive,
        inactive: textInactive
    };
    if (as == 'Menu') {
        var dropzone = /*#__PURE__*/ _jsxs(Menu, {
            children: [
                /*#__PURE__*/ _jsx(Menu.Item, {
                    fitted: true,
                    title: text.inactive,
                    children: /*#__PURE__*/ _jsxs("div", {
                        ...rootProps,
                        children: [
                            /*#__PURE__*/ _jsx("input", {
                                ...inputProps
                            }),
                            /*#__PURE__*/ _jsx(Icon, {
                                style: {
                                    fontSize: '1.9rem'
                                },
                                name: "angle double down"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Menu.Item, {
                    icon: "folder open",
                    onClick: openDialogWithMode('folder'),
                    content: "Folders"
                }),
                /*#__PURE__*/ _jsx(Menu.Item, {
                    icon: "folder open outline",
                    onClick: openDialogWithMode('files'),
                    content: "Files"
                }),
                /*#__PURE__*/ _jsx(Menu.Item, {
                    icon: "trash",
                    title: "clear files",
                    onClick: ()=>setUploaded({})
                }),
                !mimetypes && /*#__PURE__*/ _jsx(Menu.Item, {
                    title: "accept only image mimetypes",
                    active: accept !== null,
                    onClick: ()=>setAccept(accept ? null : MIME_IMAGE),
                    children: /*#__PURE__*/ _jsxs(Icon.Group, {
                        children: [
                            /*#__PURE__*/ _jsx(Icon, {
                                name: "filter"
                            }),
                            /*#__PURE__*/ _jsx(Icon, {
                                corner: true,
                                name: "image outline"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(DebugFeature, {
                    children: /*#__PURE__*/ _jsx(Menu.Item, {
                        icon: "bug",
                        disabled: files.length == 0,
                        active: showFiles,
                        onClick: ()=>setShowFiles(!showFiles)
                    })
                }),
                progressMenu,
                /*#__PURE__*/ _jsx(ProgressMenuItem, {
                    total: size(uploaded),
                    value: size(doneFiles),
                    suffix: " Files",
                    position: "right"
                }),
                moreMenu
            ]
        });
    } else {
        let inner = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("input", {
                    ...inputProps
                }),
                children,
                !children ? isDragActive ? text.active : text.inactive : null
            ]
        });
        var dropzone = /*#__PURE__*/ React.createElement(as, rootProps, inner);
        if (stopClickPropagation) {
            // double nesting to prevent click bubbling
            // we cant use onClick in rootProps as react-dropzone will catch the propagation (and will not open dlg)
            dropzone = /*#__PURE__*/ React.createElement(as, {
                onClick: (e)=>e.stopPropagation(),
                children: /*#__PURE__*/ React.createElement(as, rootProps, inner)
            });
        }
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            dropzone,
            showFiles && /*#__PURE__*/ _jsx("div", {
                children: files
            })
        ]
    });
};
